/**
 * RainShelterModule — the ROOF MAP's producer (render wave 1, 2026-09-17).
 *
 * Bakes `lib/rain_shelter.ts`'s target: an orthographic camera straight down
 * from 400 m over the player renders everything on RAIN_SHELTER_LAYER with an
 * override material that writes the fragment's ABSOLUTE height (render y −
 * scene.position.y) and alpha 1; the target clears to 0 (no roof). The
 * consumers (the ground shader, the GPU weather emitters) read the shared
 * bounds uniform this module refreshes every frame in RENDER space.
 *
 * CADENCE: bake on a re-centre (the player 24 m from the window's middle),
 * on the first frame a world is up, and every SHELTER_REBAKE_S while it
 * rains, snows or the ground is still drying; never otherwise. Off (every
 * point exposed) when the tier flag is off, in space, on no world, while an
 * interior is active (its own flag already stops the rain) and while XR
 * presents.
 *
 * ORIENTATION: the camera is rotated −90° about X, so its +Y is world −Z and
 * the map's v runs toward −Z — `shelterTexelUv` is the one place that flip
 * lives (the TSL and the CPU readback both go through it).
 *
 * Lever: `__rainShelter()` — state, `sampleAt(x, z)` (async readback of one
 * texel, absolute coordinates), `debugRoof(x, z, y, size)` (a slab on the
 * layer for probes), `bakeNow()`.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { uniform, vec4, positionWorld } from 'three/tsl';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { activePreset } from '../lib/quality_tiers.js';
import { getWetness } from '../lib/wetness.js';
import {
  RAIN_SHELTER_LAYER, SHELTER_REGION_M, SHELTER_CAM_ABOVE_M, SHELTER_CAM_FAR_M, SHELTER_REBAKE_S, SHELTER_RES,
  ensureShelterTarget, shelterBounds, shelterEnabled, shelterBoundsRender, shelterNeedsRecenter, shelterUv, shelterTexelUv, shelterExposureCpu,
} from '../lib/rain_shelter.js';

interface WeatherLike { getPrecip?: () => { kind: string; density: number } }

export class RainShelterModule implements IModule {
  readonly name = 'RainShelter';
  private app!: App;
  private rt!: THREE.RenderTarget;
  private cam!: THREE.OrthographicCamera;
  private bakeMat!: THREE.MeshBasicNodeMaterial;
  private readonly uSceneY = uniform(0);
  private centerAbs: [number, number] = [Number.NaN, Number.NaN];
  private bakes = 0;
  private lastBakeMs = 0;
  private lastOn = false;
  private readonly debugRoofs: THREE.Mesh[] = [];
  private readonly _clear = new THREE.Color();

  init(app: App): void {
    this.app = app;
    this.rt = ensureShelterTarget();
    const h = SHELTER_REGION_M / 2;
    this.cam = new THREE.OrthographicCamera(-h, h, h, -h, 0, SHELTER_CAM_FAR_M);
    this.cam.rotation.x = -Math.PI / 2; // straight down; camera +Y = world −Z (see shelterTexelUv)
    this.cam.layers.set(RAIN_SHELTER_LAYER);
    this.bakeMat = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide });
    this.bakeMat.colorNode = vec4(positionWorld.y.sub(this.uSceneY as never), 0, 0, 1);
    this.bakeMat.fog = false;
    this.bakeMat.name = 'rain-shelter-bake';
    app.world.addSystem(makeSystem('AtlasRainShelter', Phase.RenderSub, 9, () => this.tick()));
    (globalThis as unknown as { __rainShelter?: () => unknown }).__rainShelter = () => ({
      enabled: (shelterEnabled as unknown as { value: number }).value === 1,
      flag: !!activePreset().rainShelter,
      center: [this.centerAbs[0], this.centerAbs[1]],
      size: SHELTER_REGION_M,
      res: SHELTER_RES,
      bakes: this.bakes,
      lastBakeMs: this.lastBakeMs,
      bounds: shelterBounds.toArray(),
      layerMeshes: this.countLayerMeshes(),
      debugRoofs: this.debugRoofs.length,
      sampleAt: (x: number, z: number) => this.sampleAt(x, z),
      exposureAt: (x: number, y: number, z: number) => this.exposureAt(x, y, z),
      debugRoof: (x: number, z: number, y: number, size = 12) => this.debugRoof(x, z, y, size),
      bakeNow: () => { this.centerAbs = [atlas.localX, atlas.localZ]; this.bake(); },
    });
  }

  private countLayerMeshes(): number {
    let n = 0;
    this.app.world.scene.traverse((o) => { if ((o as THREE.Mesh).isMesh && o.layers.isEnabled(RAIN_SHELTER_LAYER)) n++; });
    return n;
  }

  private tick(): void {
    const p = activePreset();
    const native = this.app.renderer.native as unknown as { xr?: { isPresenting?: boolean } };
    const on = !!p.rainShelter && !atlas.spaceFlight && atlas.currentWorldId != null && !atlas.loadingGate && atlas.interiorId === null && !native.xr?.isPresenting;
    (shelterEnabled as unknown as { value: number }).value = on ? 1 : 0;
    if (!on) { this.lastOn = false; return; }
    const sp = this.app.world.scene.position;
    const px = atlas.localX, pz = atlas.localZ;
    if (!this.lastOn || Number.isNaN(this.centerAbs[0]) || shelterNeedsRecenter(this.centerAbs[0], this.centerAbs[1], px, pz)) {
      this.centerAbs = [px, pz];
      this.bake();
    } else if (performance.now() - this.lastBakeMs > SHELTER_REBAKE_S * 1000) {
      const weather = this.app.getModule('Weather') as WeatherLike | undefined;
      const pr = weather?.getPrecip?.() ?? { kind: 'none', density: 0 };
      if ((pr.kind !== 'none' && pr.density > 0) || getWetness() > 0.001) this.bake();
    }
    this.lastOn = true;
    // The bounds ride in RENDER space and follow every rebase (origin = −scene.position).
    const [minX, minZ, size] = shelterBoundsRender(this.centerAbs[0], this.centerAbs[1], -sp.x, -sp.z, SHELTER_REGION_M);
    shelterBounds.set(minX, minZ, size, sp.y);
  }

  /** One top-down render of the roof layer into the shared target. */
  private bake(): void {
    const renderer = this.app.renderer as unknown as { native: THREE.WebGPURenderer; paused?: boolean };
    if (renderer.paused) return;
    const r = renderer.native;
    const scene = this.app.world.scene;
    const sp = scene.position;
    const [cx, cz] = this.centerAbs;
    this.cam.position.set(cx + sp.x, atlas.localY + sp.y + SHELTER_CAM_ABOVE_M, cz + sp.z);
    this.cam.updateMatrixWorld(true);
    (this.uSceneY as unknown as { value: number }).value = sp.y;
    const prevRT = r.getRenderTarget();
    const prevOverride = scene.overrideMaterial;
    const prevAuto = r.autoClear;
    const prevBg = scene.background, prevBgNode = (scene as { backgroundNode?: unknown }).backgroundNode ?? null;
    r.getClearColor(this._clear as unknown as never); const prevAlpha = r.getClearAlpha(); // a Color copies the rgb of three's Color4; the alpha is restored beside it
    try {
      // The sky's background node is not layered and would paint a "roof" over the whole map.
      scene.background = null; (scene as { backgroundNode?: unknown }).backgroundNode = null;
      scene.overrideMaterial = this.bakeMat;
      r.setRenderTarget(this.rt);
      r.setClearColor(0x000000, 0);
      r.autoClear = false;
      r.clear();
      r.render(scene, this.cam);
    } finally {
      scene.overrideMaterial = prevOverride;
      scene.background = prevBg; (scene as { backgroundNode?: unknown }).backgroundNode = prevBgNode;
      r.autoClear = prevAuto;
      r.setClearColor(this._clear, prevAlpha);
      r.setRenderTarget(prevRT);
    }
    this.bakes++;
    this.lastBakeMs = performance.now();
  }

  /** Read one texel (ABSOLUTE x, z): the roof's absolute height and the coverage, or null outside the window. */
  private async sampleAt(x: number, z: number): Promise<{ roof: number; has: number; texel: [number, number] } | null> {
    const sp = this.app.world.scene.position;
    const [minX, minZ, size] = shelterBoundsRender(this.centerAbs[0], this.centerAbs[1], -sp.x, -sp.z, SHELTER_REGION_M);
    const uv = shelterUv(minX, minZ, size, x + sp.x, z + sp.z);
    if (!uv) return null;
    const [tu, tv] = shelterTexelUv(uv[0], uv[1]);
    const px = Math.min(SHELTER_RES - 1, Math.floor(tu * SHELTER_RES)), py = Math.min(SHELTER_RES - 1, Math.floor(tv * SHELTER_RES));
    const r = this.app.renderer.native as unknown as { readRenderTargetPixelsAsync: (rt: THREE.RenderTarget, x: number, y: number, w: number, h: number) => Promise<ArrayLike<number>> };
    const buf = await r.readRenderTargetPixelsAsync(this.rt, px, py, 1, 1);
    const half = buf instanceof Uint16Array;
    const v = (i: number): number => (half ? THREE.DataUtils.fromHalfFloat((buf as Uint16Array)[i]) : buf[i]);
    return { roof: v(0), has: v(3), texel: [px, py] };
  }

  private async exposureAt(x: number, y: number, z: number): Promise<number> {
    const s = await this.sampleAt(x, z);
    return s ? shelterExposureCpu(s.has, s.roof, y) : 1;
  }

  /** A grey slab on the roof layer at ABSOLUTE (x, y, z) — probes only. */
  private debugRoof(x: number, z: number, y: number, size: number): THREE.Mesh {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(size, size), new THREE.MeshStandardMaterial({ color: 0x3a3a40, roughness: 0.9 }));
    m.rotation.x = -Math.PI / 2;
    m.position.set(x, y, z);
    m.layers.enable(RAIN_SHELTER_LAYER);
    m.name = 'rain-shelter-debug-roof';
    this.app.world.scene.add(m);
    this.debugRoofs.push(m);
    return m;
  }
}
